/**
 * SPEC.md §7 — M6.4 (issue #31): the shelve invariant. Every wholesale
 * buffer replacement shelves the outgoing buffer to `plotter:history`
 * first, silently. `deriveRowTitle`/`upsertHistoryRow`/`shouldShelve` are
 * pure — no `localStorage`/`crypto` access — the impure wrappers below
 * (`readHistory`/`writeHistory`/`shelveBuffer`/`generateId`) are the only
 * parts that touch the browser, mirroring `initialSource.ts`'s own
 * pure-logic-plus-thin-wrapper shape.
 */
import { detectDiagramType, type DiagramType } from './diagramTypes';
import { readStorage, writeStorage } from './storage';

export interface HistoryRow {
  id: string;
  title: string;
  type: string;
  code: string;
  updatedAt: number;
}

export const HISTORY_STORAGE_KEY = 'plotter:history';
export const MAX_HISTORY_ROWS = 20;

// Some types (e.g. xychart-beta) quote their title: `title "Like this"`.
// Strips one matching pair of surrounding quotes if present, otherwise
// returns the text unchanged.
function stripSurroundingQuotes(text: string): string {
  const match = /^"(.*)"$/.exec(text);
  return match?.[1] ?? text;
}

function extractFrontmatterTitle(source: string): string | null {
  // trimStart(), not a bare `^---` anchor — diagramTypes.ts's own
  // extractOpeningToken skips leading blank lines before a frontmatter
  // fence, and this needs to agree with it or a diagram type detects
  // correctly while its Recent-list title silently misses the frontmatter.
  const fence = /^---\r?\n([\s\S]*?)\r?\n---/.exec(source.trimStart());
  if (fence === null) return null;
  const titleLine = /^title:\s*(.+)$/m.exec(fence[1] ?? '');
  const title = titleLine?.[1]?.trim();
  return title === undefined ? null : stripSurroundingQuotes(title);
}

// A bare `title <text>` line (no colon) — distinct from frontmatter's
// `title:` (colon immediately after the word), so this never matches
// inside a frontmatter block. About ten Mermaid types carry this directive
// (pie, gantt, sequence, journey, quadrantChart, and others) — one regex
// covers all of them, per SPEC.md §7's own framing.
function extractBodyTitleLine(source: string): string | null {
  const match = /^[ \t]*title[ \t]+(.+)$/m.exec(source);
  const title = match?.[1]?.trim();
  return title === undefined ? null : stripSurroundingQuotes(title);
}

// Not exhaustive of every Mermaid flowchart node shape (there are ~15) —
// covers the common ones: stadium/subroutine/cylinder/circle/hexagon
// (double-delimiter shapes, checked first so their outer shape wins over
// the brackets nested inside), then plain rectangle/rounded/rhombus.
// SPEC.md §7: "flowchart is the dominant type and its shape delimiters are
// stable syntax" — a deliberate, scoped exception, not a promise to parse
// every shape from every diagram type.
function extractFirstFlowchartNodeLabel(source: string): string | null {
  const doublePattern = /\(\(([^)]+)\)\)|\[\[([^\]]+)\]\]|\(\[([^\]]+)\]\)|\[\(([^)]+)\)\]|\{\{([^}]+)\}\}/;
  const singlePattern = /\[([^\]]+)\]|\(([^)]+)\)|\{([^}]+)\}/;

  for (const line of source.split('\n')) {
    const doubleMatch = doublePattern.exec(line);
    if (doubleMatch) {
      const text = doubleMatch.slice(1).find((group) => group !== undefined);
      if (text) return text.trim();
    }
    const singleMatch = singlePattern.exec(line);
    if (singleMatch) {
      const text = singleMatch.slice(1).find((group) => group !== undefined);
      if (text) return text.trim();
    }
  }
  return null;
}

/**
 * SPEC.md §7's exact precedence: `title:` frontmatter → a bare `title`
 * line in the body → the first node label for flowcharts only → the
 * diagram type name.
 */
export function deriveRowTitle(source: string, diagramType: DiagramType | null): string {
  const frontmatterTitle = extractFrontmatterTitle(source);
  if (frontmatterTitle !== null) return frontmatterTitle;

  const bodyTitle = extractBodyTitleLine(source);
  if (bodyTitle !== null) return bodyTitle;

  if (diagramType?.id === 'flowchart') {
    const nodeLabel = extractFirstFlowchartNodeLabel(source);
    if (nodeLabel !== null) return nodeLabel;
  }

  return diagramType?.label ?? 'Untitled';
}

/** Moves an existing row (by id) to the top rather than appending a twin, capped at MAX_HISTORY_ROWS. */
export function upsertHistoryRow(history: HistoryRow[], row: HistoryRow): HistoryRow[] {
  const withoutExisting = history.filter((existing) => existing.id !== row.id);
  return [row, ...withoutExisting].slice(0, MAX_HISTORY_ROWS);
}

export interface ShelveCandidate {
  code: string;
  originalCode: string;
}

/** True only when the buffer has actually been edited since it loaded. */
export function shouldShelve(buffer: ShelveCandidate): boolean {
  return buffer.code !== buffer.originalCode;
}

export function generateId(): string {
  return crypto.randomUUID();
}

function isHistoryRow(value: unknown): value is HistoryRow {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.title === 'string' &&
    typeof v.type === 'string' &&
    typeof v.code === 'string' &&
    typeof v.updatedAt === 'number'
  );
}

/** Corrupt or foreign `plotter:history` content degrades to an empty history, not a crash. */
export function readHistory(): HistoryRow[] {
  const raw = readStorage(HISTORY_STORAGE_KEY);
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHistoryRow);
  } catch {
    return [];
  }
}

export function writeHistory(history: HistoryRow[]): void {
  writeStorage(HISTORY_STORAGE_KEY, JSON.stringify(history));
}

/**
 * The one shelve flow every wholesale-replacement call site uses: derive
 * the row from the outgoing buffer's own code, upsert it into
 * `plotter:history`. Callers decide *whether* to shelve via `shouldShelve`
 * — this always shelves unconditionally once called.
 */
export function shelveBuffer(id: string, code: string): void {
  const diagramType = detectDiagramType(code);
  const row: HistoryRow = {
    id,
    title: deriveRowTitle(code, diagramType),
    type: diagramType?.id ?? 'unrecognised',
    code,
    updatedAt: Date.now(),
  };
  writeHistory(upsertHistoryRow(readHistory(), row));
}
